import {Component, inject, ViewChild} from '@angular/core';
import {VendorBalanceService} from '../../service/vendor-balance.service';
import {GridEventOutput, GridModel} from 'src/app/shared/model/page-grid';
import {VendorBalanceModel, VendorBalenceResponse} from '../../models/vendor-balances';
import {TramsDataSourceService} from 'src/app/payments/service/trams-data-source.service';
import {TramsDataSourceModel} from 'src/app/payments/models/trams-data-source';
import {AdxBookingService} from 'src/app/adx-booking/service/adx-booking.service';
import {PayStatusesResponse} from 'src/app/adx-booking/model/pay-statuses-response';
import {ModalService} from 'src/app/shared/service/modal/modal.service';
import {VendorStatementComponent} from '../vendor-statement/vendor-statement.component';
import {DatePipe} from '@angular/common';
import {PageGridComponent} from 'src/app/shared/components/page-grid/page-grid.component';

@Component({
    selector: 'app-vendor-balance',
    templateUrl: './vendor-balance.component.html',
    standalone: false
})
export class VendorBalanceComponent {
  @ViewChild(PageGridComponent) protected vendorBalanceGridRef?: PageGridComponent;

  private dataSourceService = inject(TramsDataSourceService);
  private adxBookingService = inject(AdxBookingService);
  private modalService = inject(ModalService);
  private datePipe = inject(DatePipe);

  protected readonly vendorBalanceGrid: GridModel<VendorBalenceResponse> = {
    gridDataTestId: 'vendor-balance-list',
    gridTitle: 'Vendor Balances Summary',
    gridTitleFunction: (filter: {asOfDate: Date})=> `Vendor Balance As Of ${this.datePipe.transform(filter.asOfDate, 'dd MMM y') || ''}`,
    replayButton: true,
    pdfButton: true,
    defaultSort: { active: 'vendorName', direction: 'desc' },
    columns: [
      {
        columnProperty: 'vendorName',
        columnHeader: 'Vendor Name',
        sort: true,
        clickable: true,
        clickableWithItem: (item: VendorBalanceModel) => item.hasStatements && !!this.vendorBalanceGridRef?.filters?.['asOfDate'],
        textLeft: true,
        linkEventFunction: (item:VendorBalanceModel, filter:{asOfDate: Date})=> `Vendors/${item?.id}/Statements?VendorName=${item?.vendorName}&AsOfDate=${this.datePipe.transform(filter.asOfDate, 'dd MMM y') || ''}`,
      },
      {
        columnProperty: 'id',
        columnHeader: 'Vendor Number',
        sort: true,
      },
      {
        columnProperty: 'email',
        columnHeader: 'Vendor Email',
        sort: true,
      },
      {
        columnProperty: 'phoneNumber',
        columnHeader: 'Phone Number',
        sort: true,
      },
      {
        columnProperty: 'totalBilled',
        columnHeader: 'Invoice Total',
        sort: true,
      },
      {
        columnProperty: 'outstandingBalance',
        columnHeader: 'Balance Due',
        sort: true,
      },
      {
        columnProperty: 'dataSourceName',
        columnHeader: 'Data Source',
        sort: true,
      },
    ],
    filters: [
      {
        filterProperty: 'asOfDate',
        filterDisplayName: 'As Of Date',
        filterType: 'DATE',
        filterResetValue: new Date(),
      },
      {
        filterProperty: 'vendorPayStatusId',
        filterDisplayName: 'Vendor Pay Status',
        filterType: 'DROPDOWN',
        config: {
          dropdownOptions: this.adxBookingService.getPayStatuses({isDeleted: false}),
          dropdownValueProp: 'id',
          dropdownDisplayWith: (item: PayStatusesResponse) => item.payStatusName
        }
      },
      {
        filterProperty: 'paymentDateFrom',
        filterDisplayName: 'Payment Date From',
        filterType: 'DATE',
      },
      {
        filterProperty: 'paymentDateTo',
        filterDisplayName: 'Payment Date To',
        filterType: 'DATE',
      },      {
        filterProperty: 'vendorName',
        filterDisplayName: 'Vendor Name',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'VendorBalancesView',
          autocompleteColumnName: 'VendorName',
          autocompleteEmitText: true,
        }
      },
      {
        filterProperty: 'id',
        filterDisplayName: 'Vendor Number',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'VendorBalancesView',
          autocompleteColumnName: 'Id',
          autocompleteEmitText: true,
        }
      },
      {
        filterProperty: 'minOutstandingBalance',
        filterDisplayName: 'Balance Due Min',
        filterType: 'NUMBER',
      },
      {
        filterProperty: 'maxOutstandingBalance',
        filterDisplayName: 'Balance Due Max',
        filterType: 'NUMBER',
      },
      {
        filterProperty: 'minTotalBilled',
        filterDisplayName: 'Invoice Total Min',
        filterType: 'NUMBER',
      },
      {
        filterProperty: 'maxTotalBilled',
        filterDisplayName: 'Invoice Total Max',
        filterType: 'NUMBER',
      },
      {
        filterProperty: 'dataSourceId',
        filterDisplayName: 'Data Source',
        filterType: 'DROPDOWN',
        config: {
          dropdownOptions: this.dataSourceService.getTramsDataSourceList(),
          dropdownValueProp: 'id',
          dropdownDisplayWith: (item: TramsDataSourceModel) => item.sourceName
        }
      },
    ],
    filtersInitial: {
      asOfDate: new Date(),
      vendorPayStatusId: 3,
    },
    gridGetService: inject(VendorBalanceService),
  };

  protected openBalanceDetail({item, column}: GridEventOutput<VendorBalanceModel>): void {
    if(column === 'vendorName') {
       this.modalService.open(
        VendorStatementComponent,
        {
          width: '55%',
          data: {
            asOfDate: this.datePipe.transform(this.vendorBalanceGridRef?.filters?.['asOfDate'], 'dd MMM y') ?? '',
            ...item,
          }
        },
      );
    }
  }
}
